<template>
  <v-navigation-drawer
    class="h-100 pa-0 d-flex flex-column"
    fixed
    overlay-opacity="0.7"
    right
    style="z-index: 99"
    temporary
    width="520"
    :value="visible"
    @input="checkDrawerValue"
  >
    <div v-if="isLoading" class="h-100 d-flex align-center justify-center">
      <LoaderSpinner />
    </div>
    <EmptyState
      v-if="showEmptyState"
      style="height: 100vh"
      :title="$t('assignments.address_empty_state_title')"
      :message="$t('assignments.address_empty_state_message')"
    />
    <template v-else>
      <FindingDetails
        v-if="showDetail"
        :finding="finding"
        :routine="selectedRoutine"
        :routineDefinition="routineDefinition"
        :readOnly="readOnly"
        :showRedirectionAlert="showRedirectionAlert"
        :showStatusModal="showStatusModal"
        @address="handleAddress"
        @edit="currentViewMode = VIEW_MODE.EDIT"
        @close="$emit('close')"
        @deleted="$emit('deleted')"
        @refresh="handleDetailsRefresh"
        @updateFindingStatus="handleUpdateFindingStatus"
        @changeModalStatus="handleModalStatusChanged"
        @hideModal="showStatusModal = false"
      />

      <FindingEditForm
        v-if="showEdit"
        :finding="finding"
        :routine="selectedRoutine"
        @cancel="handleFindingSaved"
        @saved="handleFindingSaved"
      />

      <AddressFinding
        v-if="showAddress"
        :addressMode="addressMode"
        :finding="finding"
        :routine="selectedRoutine"
        :routineDefinition="routineDefinition"
        @goBack="handleAddressGoBack"
        @refresh="handleAddressRefresh"
      />
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import api from '@/api'

import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import FindingEditForm from '@/components/FindingEditForm/FindingEditForm.vue'
import FindingDetails from './FindingDetails.vue'
import AddressFinding from './FindingAddressForm.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import EmptyState from '@/components/EmptyState.vue'

const VIEW_MODE = {
  DETAILS: 'details',
  EDIT: 'edit',
  ADDRESS: 'address',
}

export default {
  name: 'FindingDrawer',

  mixins: [mixin, trackerMixin],

  components: {
    AddressFinding,
    FindingDetails,
    FindingEditForm,
    LoaderSpinner,
    EmptyState,
  },

  props: {
    visible: Boolean,
    findingId: String,
    readOnly: Boolean,
    showRedirectionAlert: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currentViewMode: VIEW_MODE.DETAILS,
      finding: {},
      selectedRoutine: {},
      routineDefinition: {},
      isLoading: true,
      VIEW_MODE,
      addressMode: null,
      hasError: false,
      showStatusModal: false,
    }
  },

  watch: {
    findingId: {
      immediate: true,
      handler(newValue) {
        if (newValue?.length) this.fetchAllData()
      },
    },
  },

  computed: {
    ...mapGetters(['organizationId']),

    showAddress() {
      if (!this.findingId) return false
      return !this.isLoading && this.currentViewMode === VIEW_MODE.ADDRESS
    },

    showDetail() {
      if (!this.findingId) return false
      return !this.isLoading && this.currentViewMode === VIEW_MODE.DETAILS
    },

    showEdit() {
      if (!this.findingId) return false
      return !this.isLoading && this.currentViewMode === VIEW_MODE.EDIT
    },

    showEmptyState() {
      return !this.isLoading && (!this.findingId || this.hasError)
    },
  },

  methods: {
    checkDrawerValue(val) {
      if (val) return
      this.currentViewMode = VIEW_MODE.DETAILS
      this.addressMode = null
      this.$emit('close')
    },

    handleAddress(payload = {}) {
      this.addressMode = payload?.addressMode ?? null
      this.currentViewMode = VIEW_MODE.ADDRESS
    },

    handleAddressGoBack() {
      this.addressMode = null
      this.currentViewMode = VIEW_MODE.DETAILS
    },

    async handleFindingSaved() {
      this.isLoading = true
      await this.fetchAllData()
      this.currentViewMode = VIEW_MODE.DETAILS
      this.addressMode = null
      this.isLoading = false
      this.$emit('edited', this.finding)
    },

    async handleAddressRefresh() {
      this.isLoading = true
      await this.fetchAllData()
      this.currentViewMode = VIEW_MODE.ADDRESS
      this.isLoading = false
      this.$emit('edited', this.finding)
    },

    async handleDetailsRefresh() {
      this.isLoading = true
      await this.fetchAllData()
      this.currentViewMode = VIEW_MODE.DETAILS
      this.isLoading = false
      this.$emit('edited', this.finding)
    },

    async fetchFinding() {
      const response = await api.findings.getById({
        organizationId: this.organizationId,
        findingId: this.findingId,
      })

      this.finding = response.data
    },

    async fetchRoutine() {
      const response = await api.routine.getExecutionById(
        this.organizationId,
        this.finding.routine
      )

      this.selectedRoutine = response?.data ?? {}
    },

    async fetchRoutineDefinition() {
      const response = await api.routine.getById(
        this.organizationId,
        this.finding.routineDefinition
      )
      this.routineDefinition = response?.data ?? {}
    },

    async fetchAllData() {
      try {
        await this.fetchFinding()
        if (this.finding?.routine) await this.fetchRoutine()
        if (this.finding?.routineDefinition) await this.fetchRoutineDefinition()
      } catch (err) {
        this.handleRequestError(err)
        this.hasError = true
      } finally {
        this.isLoading = false
      }
    },

    async updateStatus(payload) {
      await api.findings.update({
        organizationId: this.organizationId,
        findingId: this.findingId,
        data: payload,
      })
      await this.fetchFinding()
    },

    async handleUpdateFindingStatus(status) {
      if (
        status == this.$constants.findings.status.canceled ||
        status == this.$constants.findings.status.onHold
      ) {
        this.showStatusModal = true
        return
      }
      if (status == this.$constants.findings.status.completed)
        return this.showError(this.$t('routines.mark_finding_as_completed'))
      await this.updateStatus({ status })
      this.trackStatusChanged(status, null)
      this.$emit('edited', this.finding)
    },

    trackStatusChanged(status, reason) {
      this.trackFindingStatusChanged(this.finding, 'selector', status, reason)
    },
    async handleModalStatusChanged(payload) {
      if (payload.status == this.$constants.findings.status.canceled) {
        await this.updateStatus({
          canceledReason: payload.reason,
          status: payload.status,
        })
        this.trackStatusChanged(payload.status, {
          canceledReason: Boolean(payload.reason),
        })
      }
      if (payload.status == this.$constants.findings.status.onHold) {
        await this.updateStatus({
          onHoldReason: payload.reason,
          status: payload.status,
        })
        this.trackStatusChanged(payload.status, {
          onHoldReason: Boolean(payload.reason),
        })
      }
      this.showStatusModal = false
      this.$emit('edited', this.finding)
    },
  },
}
</script>
